import differenceInDays from 'date-fns/differenceInDays';
import nextSaturday from 'date-fns/nextSaturday';
import isAfter from 'date-fns/isAfter';
import { useRouter } from 'next/router';
import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { trackEvent, trackSearch } from 'src/common/3rdParty/tracking';
import SearchHeroModalProvider, { type SearchHeroModal } from 'src/common/components/Search/SearchHero/SearchHeroModalProvider';
import SearchModalMobile from 'src/common/components/Search/SearchModalMobile';
import type { AutocompletePrediction } from 'src/common/components/Search/typesGoogle';
import useDefaultPlaces from 'src/common/components/Search/useDefaultPlaces';
import parseDatesQuery from 'src/common/components/Search/parseDatesQuery';
import useSearchUrl from 'src/common/components/Search/useSearchUrl';
import { NEAR_ME } from 'src/common/constants';
import useAfterRenderCallback from 'src/common/hooks/useAfterRenderCallback';
import useDateFnsLocale from 'src/common/hooks/useDateFnsLocale';
import { useMediaQueries } from 'src/common/providers/MediaQueryProvider';
import useRecentSearches from 'src/common/hooks/useRecentSearches';
import type { Breadcrumb } from 'src/common/types/api/v4/boats/search';
import { RetrievedRecentSearch, UseRecentSearchesReturn } from 'src/common/types/common/RecentSearch';
import { formatDate, todayDate } from 'src/common/utils/dateHelpers';
import reverseLookupCategoryGroupMapping from 'src/search/utils/reverseLookupCategoryGroupMapping';
import { useRequestInfoContext } from 'src/common/providers/RequestInfoProvider';

const GA4_EVENT_SEARCH = 'search';

export type SearchRefs = {
  inputs: RefObject<HTMLDivElement>;
  where: RefObject<HTMLInputElement>;
  when: RefObject<HTMLInputElement>;
  who: RefObject<HTMLInputElement>;
  button: RefObject<HTMLButtonElement>;
  capacityNumber: RefObject<HTMLInputElement>;
};

type SearchInputContextType = {
  filterDates: Date[];
  setFilterDates: Dispatch<SetStateAction<Date[]>>;
  filterDatesFormatted?: string;
  filterGuests: number;
  setFilterGuests: Dispatch<SetStateAction<number>>;
  /** Init to null to control combobox selectedItem state (undefined omits prop, ie uncontrolled) */
  filterLocation: AutocompletePrediction | null;
  setFilterLocation: Dispatch<SetStateAction<AutocompletePrediction | null | undefined>>;
  isDesktop: boolean;
  clearAll: () => void;
  /** Optionally provide params. Useful for RecentSearches which store more than 3 params. */
  submitSearch: (customSearchParams?: URLSearchParams) => void;
  isSearching: boolean;
  /**
   * `recentSearches` are returned via context,
   * so all consumers share the same `recentSearches` state
  */
  recentSearches: UseRecentSearchesReturn['recentSearches'];
  /** Updates localStorage and `recentSearches` state */
  addRecentSearch: UseRecentSearchesReturn['addRecentSearch'];
  addRecentSearchWithPrevious: UseRecentSearchesReturn['addRecentSearchWithPrevious'];
  sheet: {
    open: (name: SearchHeroModal) => void;
    close: () => void;
    active: SearchHeroModal | undefined;
  };
};

export type SearchInputProviderProps = {
  children: ReactNode;
  //* Get from pageProps because place_id param omitted when location slug exists */
  placeId?: string;
  breadcrumbs?: Breadcrumb[];
  slug?: string;
};

const SearchInputContext = createContext<SearchInputContextType | undefined>(undefined);

const SearchInputProvider: FC<SearchInputProviderProps> = ({
  children,
  placeId,
  breadcrumbs,
  slug,
}) => {
  const { nearMePlace } = useDefaultPlaces();
  const { locale } = useRouter();
  const dateFnsLocale = useDateFnsLocale();

  const {
    query: {
      dates: datesQuery,
      capacity: capacityQuery,
      slug: slugQuery,
      page,
    },
  } = useRouter();

  const { recentSearches, addRecentSearch, addRecentSearchWithPrevious } = useRecentSearches();

  /** Search page should init from url params if state not set from prev page */
  const searchParams = useMemo(() => {
    if (!page) {
      return {
        location: null,
        dates: [],
        guests: 0,
      };
    }
    let location: AutocompletePrediction | null = null;
    if (placeId === NEAR_ME) {
      location = nearMePlace;
    } else if (slug && breadcrumbs && placeId) {
      const description = breadcrumbs[breadcrumbs.length - 1].name;

      location = {
        place_id: placeId,
        description,
      };
    }
    // else:
    // Search location is set to empty when location cannot be determined.
    // If "near me" is explicitly chosen in search, it will override the current
    // location, otherwise the existing location will be kept. See "submit" below

    const dates = parseDatesQuery(datesQuery);

    return {
      location,
      dates,
      guests: parseInt(capacityQuery as string ?? '0', 10),
    };
  }, [breadcrumbs, capacityQuery, datesQuery, nearMePlace, page, placeId, slug]);

  const { recentSearch } = useRequestInfoContext();

  const defaultDates = useMemo(() => {
    if (page) {
      // page=n should always use dates params
      return searchParams.dates;
    }
    // on non-search pages, try to get dates from last recent search
    const recentDates = parseDatesQuery(recentSearch?.dates);
    if (recentDates.length) {
      return recentDates;
    }

    return [nextSaturday(todayDate())];
  }, [page, recentSearch, searchParams.dates]);

  const [filterDates, setFilterDates] = useState<SearchInputContextType['filterDates']>(defaultDates);
  const [filterGuests, setFilterGuests] = useState<SearchInputContextType['filterGuests']>(searchParams.guests);
  const [filterLocation, setFilterLocation] = useState<SearchInputContextType['filterLocation']>(searchParams.location);

  // Find location in recent search if available
  const isLocationResolved = useRef(!!searchParams.location);

  useEffect(() => {
    if (isLocationResolved.current) return;

    const recent = recentSearches.find(
      (search: RetrievedRecentSearch) => search.place_id === placeId,
    );
    if (!recent || !recent.location) return;

    setFilterLocation({
      place_id: recent.place_id,
      description: recent.location,
    });

    isLocationResolved.current = true;
  }, [locale, placeId, recentSearches, searchParams.location]);

  const { m: isDesktop } = useMediaQueries();

  const clearAll = useCallback(() => {
    setFilterDates([]);
    setFilterGuests(0);
    setFilterLocation(null);
  }, []);

  const { isSearching, redirectToSearchUrl } = useSearchUrl();

  useEffect(() => {
    // ensure state is re-initialized when slug changes on SearchPageRoot
    setFilterLocation(searchParams.location);
    setFilterDates(defaultDates);
    setFilterGuests(searchParams.guests);
  }, [
    slugQuery,
    searchParams.location,
    searchParams.dates,
    searchParams.guests,
    page,
    defaultDates,
  ]);

  const [activeSheet, setActiveSheet] = useState<SearchHeroModal | undefined>();
  const openSheet = useCallback((name) => setActiveSheet(name), []);
  const closeSheet = useCallback(() => setActiveSheet(undefined), []);

  /**
   * Call without arguments to use existing urlSearchParams (location.search)
   * @param customSearchParams completely override the search params to use
   */
  const submit = useCallback((customSearchParams?: URLSearchParams) => {
    const query = new URLSearchParams(customSearchParams ?? window.location.search);
    closeSheet();

    const defaultPlace = { place_id: nearMePlace.place_id, description: nearMePlace.description };
    const place = filterLocation ?? defaultPlace;

    // near me should override existing location geometry if it exists
    if (filterLocation?.place_id === NEAR_ME) {
      query.delete('nelon');
      query.delete('nelat');
      query.delete('swlat');
      query.delete('swlon');
    }

    // Only override if it's explicit, cater for amending search when we can't determine location
    if (filterLocation?.place_id) {
      query.set('place_id', place.place_id);
    }

    if (filterGuests) {
      query.set('capacity', filterGuests.toString());
    } else {
      query.delete('capacity');
    }

    // Either removed or replaced with multiple
    query.delete('dates');
    if (filterDates.length) {
      // Don't add multiple
      const formattedDates = filterDates.map((date) => formatDate(date, 'yyyy-MM-dd'));
      formattedDates.forEach((date) => query.append('dates', date));
    }

    if (place.place_id !== nearMePlace.place_id) {
      query.set('filters', ''); // NO_FILTERS_PARAM
    }

    addRecentSearch({
      location: filterLocation ? filterLocation.description.split(',')[0] : null,
      place_id: place.place_id,
      dates: query.getAll('dates') || filterDates.map((d) => d.toISOString()) || [],
      capacity: query.get('capacity') || (filterGuests > 0 ? filterGuests.toString() : null),
      min_price: query.get('min_price') || null,
      max_price: query.get('max_price') || null,
      instabooks: query.get('instabooks') as 'true' | null || null,
      captained: query.get('captained') as 'true' | 'false' | null || null,
      bareboat: query.get('bareboat') as 'true' | 'false' | null || null,
      superowners: query.get('superowners') as 'true' | null || null,
      categories: reverseLookupCategoryGroupMapping(query.getAll('categories') || []),
      duration: query.get('duration') || null,
      filters: query.get('filters') || undefined,
    });

    // Searches should always reset the page.
    query.delete('page');

    redirectToSearchUrl(place.place_id, query, locale);

    const gtagObj = () => {
      const date = filterDates.length ? filterDates[0] : null;
      const today = todayDate();
      const daysToDate = date ? differenceInDays(date, today) : null;
      const dateRangeOption = filterDates.reduce((count, currDate) => {
        if (date && currDate !== date && isAfter(currDate, date)) return count + 1;
        return count;
      }, 0);
      const dateRangeString = `${dateRangeOption} day${dateRangeOption > 1 || dateRangeOption === 0 ? 's' : ''}`;
      return {
        p1: place?.description,
        p2: date ? formatDate(date, 'yyyy-MM-dd') : null,
        p3: daysToDate,
        p4: dateRangeString,
        p5: filterGuests,
      };
    };
    trackEvent(GA4_EVENT_SEARCH, gtagObj());
    trackSearch({
      search_string: place.description,
      content_ids: [place.place_id],
      description: place.description,
    });
  }, [
    nearMePlace.place_id,
    nearMePlace.description,
    filterLocation,
    filterDates,
    filterGuests,
    addRecentSearch,
    redirectToSearchUrl,
    closeSheet,
    locale,
  ]);

  /** Ensures latest state available */
  const submitSearch = useAfterRenderCallback(submit);

  const filterDatesFormatted = useMemo((): string => {
    if (!filterDates?.[0]) return '';
    /** Date param, is sorted so selected date is first, allowing range to be inferred */
    const selectedDate = filterDates[0];
    let range = 0;
    filterDates.forEach((date) => {
      if (isAfter(date, selectedDate)) range += 1;
    });

    /**
     * locale option is required for date-fns to format dates in the correct language.
     * By passing locale options to formatDate, we handle the case where the locale changes
     * by language selector
     */
    const formattedDate = formatDate(selectedDate, 'LLL d', dateFnsLocale);
    return range ? `${formattedDate} ± ${range}` : formattedDate;
  }, [filterDates, dateFnsLocale]);

  const contextValue = useMemo(() => ({
    filterDates,
    setFilterDates,
    filterDatesFormatted,
    filterGuests,
    setFilterGuests,
    filterLocation,
    setFilterLocation,
    recentSearches,
    addRecentSearch,
    addRecentSearchWithPrevious,
    clearAll,
    isDesktop,
    submitSearch,
    isSearching,
    sheet: {
      open: openSheet,
      close: closeSheet,
      active: activeSheet,
    },
  }), [
    filterDates,
    filterDatesFormatted,
    filterGuests,
    filterLocation,
    recentSearches,
    addRecentSearch,
    addRecentSearchWithPrevious,
    clearAll,
    isDesktop,
    submitSearch,
    isSearching,
    openSheet,
    closeSheet,
    activeSheet,
  ]);

  return (
    <SearchInputContext.Provider value={contextValue}>
      <SearchHeroModalProvider active={activeSheet} openType="in-sheet">
        <SearchModalMobile />
      </SearchHeroModalProvider>
      {children}
    </SearchInputContext.Provider>
  );
};
export default SearchInputProvider;

export const useSearchInputContext = (): SearchInputContextType => {
  const context = useContext(SearchInputContext);

  if (!context) {
    throw new Error('useSearchInputContext must be used within a SearchInputProvider');
  }
  return context;
};
